import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PanToolIcon from "@mui/icons-material/PanTool";
import EditIcon from "@mui/icons-material/Edit";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import { ip_back } from "../config";
import { useBrand } from "../context/BrandContext";
import { pickNationalRanking } from "../utils/nationalRanking";
import { formatRankingPlace } from "../utils/flttRankingDisplay";

const toAbsoluteImageUrl = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("/")) return `${ip_back.replace(/\/$/, "")}${trimmed}`;
  return `${ip_back}${trimmed.replace(/^\/+/, "")}`;
};

function formatMemberDate(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatLastSession(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return "Today";
  if (diffDays === 2) return "Yesterday";
  if (diffDays < 7) return `${diffDays - 1}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function getObjectiveLabel(goal, lang) {
  if (!goal || typeof goal !== "object") return "";
  if (goal.custom_text && String(goal.custom_text).trim()) {
    return String(goal.custom_text).trim();
  }
  const langMap = {
    en: ["translated_name_en", "attribute_name_en", "name_en"],
    fr: ["translated_name_fr", "attribute_name_fr", "name_fr"],
    de: ["translated_name_de", "attribute_name_de", "name_de"],
    es: ["translated_name_es", "attribute_name_es", "name_es"],
    nl: ["translated_name_nl", "attribute_name_nl", "name_nl"],
    pt: ["translated_name_pt", "attribute_name_pt", "name_pt"],
  };
  const preferred = langMap[lang] || langMap.en;
  const fallback = [
    "translated_name",
    "attribute_name",
    "name",
    "translated_name_en",
    "attribute_name_en",
    "name_en",
  ];
  for (const key of preferred) {
    if (goal[key]) return goal[key];
  }
  for (const key of fallback) {
    if (goal[key]) return goal[key];
  }
  return goal.translated_name || goal.attribute_name || "";
}

function levelDisplay(user, t) {
  const hasValidLevel =
    user.player_level &&
    typeof user.player_level === "object" &&
    (user.player_level.level_name || user.player_level.level_description);

  if (hasValidLevel || (typeof user.player_level === "string" && user.player_level)) {
    if (typeof user.player_level === "string") return user.player_level;
    const levelName = user.player_level?.level_name;
    if (levelName) {
      if (user.main_purpose_id && user.main_purpose_id !== 3) {
        switch (levelName) {
          case "C3":
            return t("beginner");
          case "B1":
            return t("intermediate");
          case "A1":
            return t("advanced");
          default:
            return levelName;
        }
      }
      return levelName;
    }
    if (user.player_level?.level_description) return user.player_level.level_description;
  }
  const clubClassement = pickNationalRanking(user)?.classement;
  if (clubClassement) return clubClassement;
  return "—";
}

function styleDisplay(user) {
  if (!user.style) return "—";
  if (user.style.translations?.[0]?.name) return user.style.translations[0].name;
  if (user.style.style_name) return user.style.style_name;
  return "—";
}

function gripLabel(grip, t) {
  if (!grip) return "—";
  switch (grip) {
    case "shakehand":
      return t("shakehand");
    case "penhold":
      return t("penhold");
    default:
      return grip;
  }
}

function handLabel(hand, t) {
  if (!hand) return "—";
  switch (hand) {
    case "right":
      return t("rightHand");
    case "left":
      return t("leftHand");
    default:
      return hand;
  }
}

function GripIcon({ grip }) {
  if (grip === "penhold") return <EditIcon sx={{ fontSize: 16, opacity: 0.72 }} />;
  return <PanToolIcon sx={{ fontSize: 16, opacity: 0.72 }} />;
}

/**
 * Single-panel concise player snapshot for coach modal (no per-attribute cards).
 */
export function PlayerProfileConcise({ user }) {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const [imgFailed, setImgFailed] = useState(false);
  const lang = (i18n.language || "en").split("-")[0];

  const primary = brand?.color_primary || "#0877BC";
  const primaryDark = brand?.color_primary_dark || "#065a8f";

  useEffect(() => {
    setImgFailed(false);
  }, [user?.user_id, user?.user_image]);

  const displayName =
    user.user_alias != null && user.user_alias !== ""
      ? user.user_alias
      : user.user_first_name || "—";
  const email = user.user_email || "";
  const clubName =
    user.club?.club_name || user.club_details?.club_name || "—";

  const avatarSrc = useMemo(() => {
    const raw = user.user_image;
    return toAbsoluteImageUrl(raw) || (raw && !String(raw).startsWith("http") ? raw : null);
  }, [user.user_image]);

  const ranking = pickNationalRanking(user, brand);
  const level = ranking?.classement || levelDisplay(user, t);
  const pts = ranking?.ranking_points ?? user.fltt_ranking_points;
  const ptsRounded =
    pts !== null && pts !== undefined && !Number.isNaN(Number(pts))
      ? Math.round(Number(pts))
      : null;
  const placeLabel = formatRankingPlace(ranking?.ranking_place);

  const style = styleDisplay(user);
  const grip = gripLabel(user.player_grip, t);
  const hand = handLabel(user.player_hand, t);
  const showPlayingDetails =
    (style !== "—" && user.style) ||
    (grip !== "—" && user.player_grip) ||
    (hand !== "—" && user.player_hand);

  const primaryGoal = user.userFocuses?.primary;
  const secondaryGoals = user.userFocuses?.secondary || [];
  const primaryText = primaryGoal ? getObjectiveLabel(primaryGoal, lang) : "";
  const secondaryText = secondaryGoals
    .map((g) => getObjectiveLabel(g, lang))
    .filter(Boolean)
    .join(", ");

  const hasObjectives = primaryText || secondaryText;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* Identity */}
      <Box
        sx={{
          background: `linear-gradient(125deg, ${alpha(primary, 0.12)} 0%, ${alpha(primaryDark, 0.07)} 55%, ${alpha(primary, 0.03)} 100%)`,
          px: { xs: 2, sm: 2.5 },
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar
          src={imgFailed ? undefined : avatarSrc || undefined}
          alt=""
          onError={() => setImgFailed(true)}
          sx={{
            width: { xs: 68, sm: 76 },
            height: { xs: 68, sm: 76 },
            fontSize: "1.5rem",
            fontWeight: 700,
            border: "3px solid #fff",
            boxShadow: `0 4px 14px ${alpha(primary, 0.35)}`,
            bgcolor: primary,
          }}
        >
          {(displayName || "?").slice(0, 1).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, letterSpacing: -0.3 }}>
            {displayName}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center", mt: 1 }}>
            {level !== "—" && (
              <Chip
                size="small"
                label={level}
                sx={{
                  height: 26,
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  bgcolor: alpha("#fff", 0.92),
                  color: primaryDark,
                  border: `1px solid ${alpha(primary, 0.28)}`,
                }}
              />
            )}
            {ptsRounded !== null && (
              <Chip
                size="small"
                variant="outlined"
                label={`${ptsRounded} pts`}
                sx={{ height: 26, fontSize: "0.75rem", borderColor: alpha(primary, 0.35) }}
              />
            )}
            {placeLabel && (
              <Chip
                size="small"
                variant="outlined"
                label={placeLabel}
                sx={{ height: 26, fontSize: "0.75rem", borderColor: alpha(primary, 0.35) }}
              />
            )}
          </Box>
          {email ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.85 }} noWrap>
              {email}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75, mb: 0, lineHeight: 1.5 }}>
            {clubName}
            <Box component="span" sx={{ mx: 0.75, opacity: 0.4 }}>
              ·
            </Box>
            {t("registrationDate")} {formatMemberDate(user.createdAt)}
            <Box component="span" sx={{ mx: 0.75, opacity: 0.4 }}>
              ·
            </Box>
            {t("lastSession")} {formatLastSession(user.user_last_session)}
          </Typography>
        </Box>
      </Box>

      {showPlayingDetails && (
        <>
          <Divider />
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
              <SportsTennisIcon sx={{ fontSize: 20, color: primary, opacity: 0.88 }} />
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, letterSpacing: 1.1, color: "text.secondary", fontSize: "0.68rem" }}
              >
                {t("playingProfile") || "Playing profile"}
              </Typography>
            </Box>
            <Box
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              {style !== "—" && (
                <>
                  <DetailLine label={t("playingStyle")} value={style} />
                  <Divider sx={{ borderColor: "divider" }} />
                </>
              )}
              <DetailLine
                label={`${t("racketGrip")} · ${t("playingHand")}`}
                value={
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
                    <GripIcon grip={user.player_grip} />
                    <span>
                      {grip} · {hand}
                    </span>
                  </Box>
                }
              />
            </Box>
          </Box>
        </>
      )}

      {hasObjectives && (
        <>
          <Divider />
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.75,
              background: `linear-gradient(180deg, ${alpha(primary, 0.045)} 0%, transparent 100%)`,
            }}
          >
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 1, color: primary, fontSize: "0.68rem" }}
            >
              {t("trainingGoals") || "Training goals"}
            </Typography>
            {primaryText ? (
              <Typography variant="body2" sx={{ mt: 1.1, fontWeight: 600, lineHeight: 1.5 }}>
                <Box component="span" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.7rem", display: "block", mb: 0.35 }}>
                  {t("primaryFocus") || "Primary"}
                </Box>
                {primaryText}
              </Typography>
            ) : null}
            {secondaryText ? (
              <Typography variant="body2" sx={{ mt: primaryText ? 1.15 : 1.1, color: "text.secondary", lineHeight: 1.55, fontSize: "0.875rem" }}>
                <Box component="span" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.disabled", display: "block", mb: 0.35 }}>
                  {t("secondaryFocuses") || "Secondary"}
                </Box>
                {secondaryText}
              </Typography>
            ) : null}
          </Box>
        </>
      )}
    </Paper>
  );
}

function DetailLine({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        px: 1.5,
        py: 1.2,
        bgcolor: (theme) => alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.12 : 0.5),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0, pt: 0.15 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}
